import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { DateRange } from 'react-date-range';
import { es } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useAlert } from '../../../../context/AlertContext';
import { useCompany } from '../../../../context/CompanyContext';
import { generateISLRXml } from '../../../../utils/xmlGenerator';

const ISLRExportModal = ({ show, onHide, data = [] }) => {
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection'
        }
    ]);
    const [loading, setLoading] = useState(false);
    const { showToast } = useAlert();
    const { company } = useCompany();

    const handleSelect = (ranges) => {
        setDateRange([ranges.selection]);
    };

    const getXmlData = () => {
        const { startDate, endDate } = dateRange[0];
        if (!startDate || !endDate) return null;

        // Normalizar fechas para comparación (inicio del día y fin del día)
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        const itemsInRange = data.filter(d => {
            if (!d.fecha) return false;
            // Parsear fecha del documento (asumiendo string YYYY-MM-DD o ISO)
            // Ajustar según el formato real de 'd.fecha' en tu DB.
            // Si viene con T00:00:00.000Z, new Date() lo maneja bien.
            const itemDate = new Date(d.fecha);
            return itemDate >= start && itemDate <= end;
        });

        return itemsInRange;
    };

    const generateXmlBlob = async () => {
        const items = getXmlData();
        if (!items || items.length === 0) return null;
        const xmlString = generateISLRXml(items, { rif: company?.rif });
        return new Blob([xmlString], { type: 'application/xml' });
    };

    const handlePreview = async () => {
        setLoading(true);
        try {
            const blob = await generateXmlBlob();
            if (!blob) {
                showToast('No hay retenciones en el rango seleccionado', 'warning');
                setLoading(false);
                return;
            }
            const text = await blob.text();

            const newWindow = window.open('', '_blank');
            if (newWindow) {
                newWindow.document.write(`
                    <html>
                        <head>
                            <title>Vista Previa XML ISLR</title>
                            <style>
                                body { font-family: monospace; padding: 20px; background: #f8f9fa; margin: 0; }
                                textarea { width: 100%; height: 98vh; border: 1px solid #ccc; padding: 10px; border-radius: 5px; background: white; resize: none; }
                            </style>
                        </head>
                        <body>
                            <textarea readonly>${text}</textarea>
                        </body>
                    </html>
                `);
                newWindow.document.close();
            } else {
                showToast('Habilite pop-ups para ver la vista previa', 'warning');
            }
        } catch (error) {
            console.error(error);
            showToast('Error al generar vista previa', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        setLoading(true);
        try {
            const blob = await generateXmlBlob();
            if (!blob) {
                showToast('No hay retenciones en el rango seleccionado', 'warning');
                return;
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const { startDate, endDate } = dateRange[0];
            const startStr = startDate.toLocaleDateString('es-VE').replace(/\//g, '-');
            const endStr = endDate.toLocaleDateString('es-VE').replace(/\//g, '-');
            const filename = `ISLR_XML_${startStr}_${endStr}.xml`;

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            showToast('XML descargado exitosamente', 'success');
            onHide();
        } catch (error) {
            console.error(error);
            showToast('Error al descargar XML', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const count = getXmlData()?.length || 0;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold fs-5">Exportar XML Seniat (ISLR)</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3 d-flex flex-column align-items-center">
                <p className="text-muted small mb-3 text-center">
                    Seleccione el rango de fechas exacto para incluir en el archivo.
                </p>

                <div className="border rounded p-2 bg-light mb-3 d-flex justify-content-center" style={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <div style={{ transform: 'scale(0.95)', transformOrigin: 'top center' }}>
                        <DateRange
                            locale={es}
                            ranges={dateRange}
                            onChange={handleSelect}
                            moveRangeOnFirstSelection={false}
                            months={1}
                            direction="horizontal"
                            rangeColors={['#0d6efd']}
                        />
                    </div>
                </div>

                <div className="alert alert-primary text-center" role="alert" style={{ width: '100%', maxWidth: '350px' }}>
                    <small className="fw-bold d-block text-uppercase">Documentos encontrados</small>
                    <span className="fs-2 fw-bold">
                        {count}
                    </span>
                </div>

            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 mt-2 d-flex flex-column-reverse flex-md-row gap-2 text-center text-md-end">
                <Button variant="light" onClick={onHide} disabled={loading} className="w-100 w-md-auto">
                    Cancelar
                </Button>
                <Button variant="outline-primary" onClick={handlePreview} disabled={loading || count === 0} className="w-100 w-md-auto">
                    <i className="bi bi-eye me-2"></i> Vista Previa
                </Button>
                <Button variant="primary" onClick={handleDownload} disabled={loading || count === 0} className="w-100 w-md-auto">
                    {loading ? 'Generando...' : <><i className="bi bi-download me-2"></i> Descargar XML</>}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ISLRExportModal;
